import BaseController from "confinus_ui/base_controller";
import { showModal } from "lib/overlay/modal";

type VoiceOrderState =
  | "idle"
  | "recording"
  | "stopping"
  | "transcribing"
  | "reviewing";

const MAX_RECORDING_SECONDS = 120;

export default class VoiceOrderController extends BaseController {
  public static targets = [
    "recordButton",
    "stopButton",
    "statusText",
    "transcriptField",
    "submitButton",
    "audioPlayback",
    "audioElement",
    "loadingSpinner",
    "transcriptSection",
    "actionButtons",
    "recordingTimer",
  ];

  public static values = {
    transcribeUrl: { type: String, default: "" },
    processUrl: { type: String, default: "" },
  };

  declare private readonly recordButtonTarget: HTMLButtonElement;
  declare private readonly stopButtonTarget: HTMLButtonElement;
  declare private readonly statusTextTarget: HTMLElement;
  declare private readonly transcriptFieldTarget: HTMLTextAreaElement;
  declare private readonly submitButtonTarget: HTMLButtonElement;
  declare private readonly audioPlaybackTarget: HTMLElement;
  declare private readonly audioElementTarget: HTMLAudioElement;
  declare private readonly loadingSpinnerTarget: HTMLElement;
  declare private readonly transcriptSectionTarget: HTMLElement;
  declare private readonly actionButtonsTarget: HTMLElement;
  declare private readonly recordingTimerTarget: HTMLElement;

  declare private readonly transcribeUrlValue: string;
  declare private readonly processUrlValue: string;

  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private state: VoiceOrderState = "idle";
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private recordingSeconds: number = 0;
  private audioBlob: Blob | null = null;

  connect(): void {
    super.connect();
    this.setState("idle");
  }

  disconnect(): void {
    this.stopTimer();
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop();
    }
    super.disconnect();
  }

  async record(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioChunks = [];

      // Try webm first, fall back to other formats
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

      this.mediaRecorder = new MediaRecorder(stream, { mimeType });

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        this.handleRecordingComplete();
      };

      this.mediaRecorder.start(1000); // Collect data every second
      this.setState("recording");
      this.startTimer();
    } catch (err) {
      const error = err as Error;
      if (error.name === "NotAllowedError") {
        this.showStatus(
          "Microphone access denied. Please allow microphone access and try again.",
          "error",
        );
      } else {
        this.showStatus(
          "Could not access microphone. Please check your browser settings.",
          "error",
        );
      }
    }
  }

  stop(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.setState("stopping");
      this.stopTimer();
      this.mediaRecorder.stop();
    }
  }

  rerecord(): void {
    this.audioBlob = null;
    this.audioChunks = [];
    this.setState("idle");
  }

  async submitTranscript(): Promise<void> {
    const transcript = this.transcriptFieldTarget.value.trim();
    if (!transcript) {
      this.showStatus("Please record or type your order first.", "error");
      return;
    }

    // Submit as a regular form POST to the process endpoint
    const form = document.createElement("form");
    form.method = "POST";
    form.action = this.processUrlValue;

    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "authenticity_token";
    csrfInput.value = this.csrfToken;
    form.appendChild(csrfInput);

    const transcriptInput = document.createElement("input");
    transcriptInput.type = "hidden";
    transcriptInput.name = "transcript";
    transcriptInput.value = transcript;
    form.appendChild(transcriptInput);

    document.body.appendChild(form);
    form.submit();
  }

  private handleRecordingComplete(): void {
    const mimeType = this.mediaRecorder?.mimeType || "audio/webm";
    this.audioBlob = new Blob(this.audioChunks, { type: mimeType });

    // Show audio playback
    const audioUrl = URL.createObjectURL(this.audioBlob);
    this.audioElementTarget.src = audioUrl;
    this.audioPlaybackTarget.classList.remove("hidden");

    // Automatically transcribe
    this.transcribeAudio();
  }

  private async transcribeAudio(): Promise<void> {
    if (!this.audioBlob) return;

    this.setState("transcribing");

    try {
      const formData = new FormData();
      const extension = this.audioBlob.type.includes("webm") ? "webm" : "mp4";
      formData.append("audio_file", this.audioBlob, `recording.${extension}`);

      const response = await fetch(this.transcribeUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          Accept: "application/json",
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Transcription failed");
      }

      const data = await response.json();
      this.transcriptFieldTarget.value = data.transcript;
      this.setState("reviewing");
    } catch (err) {
      const error = err as Error;
      this.showStatus(
        `Transcription failed: ${error.message}. You can type your order manually below.`,
        "error",
      );
      this.setState("reviewing");
    }
  }

  private setState(state: VoiceOrderState): void {
    this.state = state;

    // Reset visibility
    this.recordButtonTarget.classList.add("hidden");
    this.stopButtonTarget.classList.add("hidden");
    this.loadingSpinnerTarget.classList.add("hidden");
    this.transcriptSectionTarget.classList.add("hidden");
    this.actionButtonsTarget.classList.add("hidden");
    this.recordingTimerTarget.classList.add("hidden");

    switch (state) {
      case "idle":
        this.recordButtonTarget.classList.remove("hidden");
        this.audioPlaybackTarget.classList.add("hidden");
        this.showStatus("Press the microphone to start recording your order.");
        break;
      case "recording":
        this.stopButtonTarget.classList.remove("hidden");
        this.recordingTimerTarget.classList.remove("hidden");
        this.showStatus("Recording... speak your order clearly.");
        break;
      case "stopping":
        this.showStatus("Processing recording...");
        break;
      case "transcribing":
        this.loadingSpinnerTarget.classList.remove("hidden");
        this.showStatus("Transcribing your order...");
        break;
      case "reviewing":
        this.transcriptSectionTarget.classList.remove("hidden");
        this.actionButtonsTarget.classList.remove("hidden");
        this.recordButtonTarget.classList.remove("hidden");
        this.showStatus(
          "Review your order below. Edit if needed, then click 'Find Products'.",
        );
        break;
    }
  }

  private showStatus(message: string, type: "info" | "error" = "info"): void {
    this.statusTextTarget.textContent = message;
    this.statusTextTarget.className = `text-center text-sm mb-4 ${
      type === "error" ? "text-red-600" : "text-gray-600"
    }`;
  }

  private startTimer(): void {
    this.recordingSeconds = 0;
    this.updateTimerDisplay();
    this.timerInterval = setInterval(() => {
      this.recordingSeconds++;
      this.updateTimerDisplay();

      if (this.recordingSeconds >= MAX_RECORDING_SECONDS) {
        this.showStatus("Maximum recording time reached (2 minutes).");
        this.stop();
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private updateTimerDisplay(): void {
    const minutes = Math.floor(this.recordingSeconds / 60);
    const seconds = this.recordingSeconds % 60;
    this.recordingTimerTarget.textContent = `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  private get csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") || "" : "";
  }
}
